import { useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router';
import { useAuth } from '../../context';
import { 
    storeAuthReturnUrl, 
    getAndClearAuthReturnUrl,
    getAuthReturnUrl,
    getReturnUrlParam 
} from '../../util/authRedirect';

/**
 * Component to handle authentication redirects for action URLs from email notifications
 * 
 * This component:
 * 1. Detects when user lands on a page with action URL params but is not authenticated
 * 2. Stores the action URL and opens auth modal with correct user type
 * 3. After login, verifies the logged-in user matches the expected recipient
 * 4. Redirects to the stored action URL if credential matches, shows error if not
 */
export function AuthRedirectHandler() {
    const auth = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // Check if current URL has action URL parameters (from email notifications)
    const hasActionParams = searchParams.has('openDetails') || 
                           searchParams.has('openReview') || 
                           searchParams.has('openQuotes') || 
                           searchParams.has('openChat');

    // Handle action URLs when user is not authenticated
    useEffect(() => {
        // When recipientId is present, the destination page handles the link (redirect to home with error
        // if wrong account or not logged in). Do not open auth modal here.
        if (searchParams.has('recipientId')) {
            return;
        }
        // Only check if we have action params and user is not authenticated
        if (hasActionParams && !auth.isLogin()) {
            // Build the full URL with all params
            const fullUrl = location.pathname + location.search;
            
            // Store the return URL
            storeAuthReturnUrl(fullUrl);
            
            // Get recipientType from URL params to open correct auth modal
            const recipientType = searchParams.get('recipientType');
            const recipientId = searchParams.get('recipientId');
            
            // Determine user type from recipientType param, default to customer
            const userType = recipientType === 'worker' ? 'worker' : 'customer';
            
            // Trigger the login modal with correct type and login-only mode
            // (disable signup for email notification links)
            window.dispatchEvent(new CustomEvent('openAuthModal', { 
                detail: { 
                    type: userType,
                    mode: 'login',
                    loginOnly: true,
                    expectedUserId: recipientId ? parseInt(recipientId, 10) : undefined
                } 
            }));
        }
    }, [hasActionParams, auth.isLogin(), location.pathname, location.search, searchParams]);

    // Handle redirect after authentication
    useEffect(() => {
        if (auth.isLogin() && auth.token && auth.user?.id) {
            // Check if we have a stored return URL (not for openSettings, which is handled in NotificationsPage)
            const returnUrl = getAuthReturnUrl();
            
            if (returnUrl && !returnUrl.includes('openSettings=true')) {
                // Get recipientId from stored URL to verify credential match
                const recipientIdParam = getReturnUrlParam(returnUrl, 'recipientId');
                
                if (recipientIdParam) {
                    const recipientId = parseInt(recipientIdParam, 10);
                    const currentUserId = auth.user.id;
                    
                    // Check if the logged-in user matches the expected recipient
                    if (recipientId !== currentUserId) {
                        // Credential mismatch - show error and clear the return URL
                        getAndClearAuthReturnUrl();
                        
                        // Dispatch event to show credential mismatch error
                        window.dispatchEvent(new CustomEvent('authCredentialMismatch', {
                            detail: {
                                message: 'This notification belongs to a different account. Please log in with the correct account to view these details.'
                            }
                        }));
                        return;
                    }
                }
                
                // Clear the stored return URL
                const actionUrl = getAndClearAuthReturnUrl();
                
                if (actionUrl) {
                    // Parse the URL to get pathname and search params
                    const urlParts = actionUrl.split('?');
                    const pathname = urlParts[0];
                    const search = urlParts[1] || '';
                    
                    // Small delay to ensure page is fully loaded and any other redirects have completed
                    setTimeout(() => {
                        // Only navigate if we're not already on that path (to avoid unnecessary navigation)
                        const currentPath = location.pathname + location.search;
                        if (currentPath !== actionUrl) {
                            // Navigate to the stored action URL
                            if (search) {
                                navigate(`${pathname}?${search}`, { replace: true });
                            } else {
                                navigate(pathname, { replace: true });
                            }
                        }
                    }, 300);
                }
            }
        }
    }, [auth.isLogin(), auth.token, auth.user?.id, navigate, location.pathname, location.search]);

    return null; // This component doesn't render anything
}

